import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  useRetrieveBreatheExercise,
  useRetrievePostContent,
} from "../../hooks/useRetrieveBreatheExercise";
import CommonContent from "./components/CommonContent/CommonContent";
import RainbowContent from "./components/RainbowContent/RainbowContent";
import EmojiContent from "./components/EmojiContent/EmojiContent";
import ModeDialog from "./components/ModeDialog/ModeDialog";
import GuidedMeditationContent from "./components/GuidedMeditationContent/GuidedMeditationContent";
import MindfulMomentContent from "./components/MindfulMomentContent/MindfulMomentContent";
import SessionModal from "./components/SessionModal/SessionModal";
import "./BreatheExcercise.scss";
import { yetiBlink } from "../../appAssets/jsons";
import closeIcon from "../../appAssets/closeIcon.png";
import { trackEvent } from "../../../mixpanel";
import { TrackingEvents } from "../../Utilities/analytics";
import Lottie from "lottie-react";

type SurpriseVariant = "guidedMeditation" | "mindfulMoment";

function BreatheExcercise() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const mode = searchParams.get("mode");
  const isDance = mode === "dance";
  const isMusic = mode === "music";

  const { data: breatheContent, loading } = useRetrieveBreatheExercise({
    isDance,
    isMusic,
  });

  const { postRetrieveContent } = useRetrievePostContent();

  // ── Mode selection (shown when mode param is absent)
  const [showModeDialog, setShowModeDialog] = useState(mode === null);
  const [isSurprise, setIsSurprise] = useState(false);
  const [surpriseVariant, setSurpriseVariant] =
    useState<SurpriseVariant | null>(null);
  // For mindful moment: 'prompt' shows the title + "Let's start!" button;
  // 'listening' shows the timer + audio playback.
  const [mindfulPhase, setMindfulPhase] = useState<"prompt" | "listening">(
    "prompt",
  );

  const [currentScreen, setCurrentScreen] = useState(0);
  const [timer, setTimer] = useState(60);
  const [selectedEmoji, setSelectedEmoji] = useState<string | null>(null);
  const [isAudioPlaying, setIsAudioPlaying] = useState(true);
  const [showSessionModal, setShowSessionModal] = useState(false);
  const [sessionModalType, setSessionModalType] = useState<
    "breathe" | "guided"
  >("breathe");
  const [timerRestartKey, setTimerRestartKey] = useState(0);

  // Mirrors mobile useIsFocused — pause audio when tab/window hidden
  useEffect(() => {
    const onVisibility = () => setIsAudioPlaying(!document.hidden);
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, []);

  // ── Auto-advance screens (only after mode is selected)
  useEffect(() => {
    if (!breatheContent || showModeDialog || showSessionModal) return;

    // Screens 0 and 1 always auto-advance using exercise duration
    if (currentScreen === 0 || currentScreen === 1) {
      const exercise = breatheContent.exercises[currentScreen];
      if (!exercise) return;
      const timeoutDuration = (parseInt(exercise.duration, 10) + 2000) / 1000;
      setTimer(timeoutDuration);

      const intervalId = setInterval(() => {
        setTimer((prev) => {
          if (prev <= 1) clearInterval(intervalId);
          return prev - 1;
        });
      }, 1000);

      const timerId = setTimeout(() => {
        trackEvent(TrackingEvents.calm_corner_exercise_complete, {
          exercise_id: currentScreen + 1,
          exercise_title: exercise.title,
        });
        setCurrentScreen((prev) => prev + 1);
      }, timeoutDuration * 1000);

      return () => {
        clearTimeout(timerId);
        clearInterval(intervalId);
      };
    }

    // Screen 2 — behaviour depends on mode
    if (currentScreen === 2) {
      // Guided meditation: no auto-advance; teacher ends the video
      if (isSurprise && surpriseVariant === "guidedMeditation") return;

      // Mindful moment prompt: no auto-advance; user taps "Let's start!"
      if (
        isSurprise &&
        surpriseVariant === "mindfulMoment" &&
        mindfulPhase === "prompt"
      )
        return;

      // Mindful moment listening: 60-second countdown then show session modal
      if (
        isSurprise &&
        surpriseVariant === "mindfulMoment" &&
        mindfulPhase === "listening"
      ) {
        const duration = 60;
        setTimer(duration);
        const intervalId = setInterval(() => {
          setTimer((prev) => {
            if (prev <= 1) clearInterval(intervalId);
            return prev - 1;
          });
        }, 1000);
        const timerId = setTimeout(() => {
          setSessionModalType("breathe");
          setShowSessionModal(true);
        }, duration * 1000);
        return () => {
          clearTimeout(timerId);
          clearInterval(intervalId);
        };
      }

      // Regular mindful breathing: rainbow breathing with showUntil timer
      const exercise = breatheContent.exercises[2];
      if (!exercise) return;
      const timeoutDuration =
        exercise.showUntil != null
          ? exercise.showUntil
          : (parseInt(exercise.duration, 10) + 2000) / 1000;
      setTimer(timeoutDuration);

      const intervalId = setInterval(() => {
        setTimer((prev) => {
          if (prev <= 1) clearInterval(intervalId);
          return prev - 1;
        });
      }, 1000);

      const timerId = setTimeout(() => {
        trackEvent(TrackingEvents.calm_corner_exercise_complete, {
          exercise_id: 3,
          exercise_title: exercise.title,
        });
        setSessionModalType("breathe");
        setShowSessionModal(true);
      }, timeoutDuration * 1000);

      return () => {
        clearTimeout(timerId);
        clearInterval(intervalId);
      };
    }

    // Screen 3
    if (currentScreen === 3) {
      // Surprise + guided meditation: screen 3 is the emoji screen, no auto-advance
      // if (isSurprise && surpriseVariant === "guidedMeditation") return;

      const exercise = breatheContent.exercises[3];
      if (isDance || isMusic) return;
      // If exercises[3] is absent (can happen in surprise mindful moment), skip to emoji
      if (!exercise) {
        const timerId = setTimeout(() => setCurrentScreen(4), 1500);
        return () => clearTimeout(timerId);
      }
      const timerId = setTimeout(() => {
        trackEvent(TrackingEvents.calm_corner_exercise_complete, {
          exercise_id: 4,
          exercise_title: exercise.title,
        });
        setCurrentScreen(4);
      }, 3000);

      return () => clearTimeout(timerId);
    }
  }, [
    currentScreen,
    breatheContent,
    isDance,
    isMusic,
    isSurprise,
    surpriseVariant,
    mindfulPhase,
    showModeDialog,
    showSessionModal,
    timerRestartKey,
  ]);

  useEffect(() => {
    if (currentScreen === 4) {
      const payload = new FormData();
      payload.append("contentType", "SELActivity");
      payload.append(
        "contentSubType",
        isDance ? "Dance" : isMusic ? "Music" : "Corner",
      );
      postRetrieveContent(payload);
    }
  }, [currentScreen]);

  const handleEndSession = () => {
    setShowSessionModal(false);
    setCurrentScreen((prev) => (isDance || isMusic ? 4 : prev + 1));
  };

  const handleContinue = () => {
    setShowSessionModal(false);
    setTimerRestartKey((k) => k + 1);
  };

  const handleClose = () => {
    trackEvent(TrackingEvents.object_close_icon_clicked, {
      from: isDance
        ? "dance"
        : isMusic
          ? "music"
          : isSurprise
            ? "surprise"
            : "calm_corner",
    });
    navigate(-1);
  };

  const handleModeSelect = (selected: "mindfulBreathing" | "surpriseMe") => {
    setShowModeDialog(false);
    if (selected === "surpriseMe") {
      setIsSurprise(true);
      const variant: SurpriseVariant =
        Math.random() < 0.5 ? "guidedMeditation" : "mindfulMoment";
      setSurpriseVariant(variant);
    }
    // "mindfulBreathing" → existing flow, no extra state needed
  };

  const renderContent = () => {
    if (!breatheContent) return null;

    // ── Surprise: guided meditation ──────────────────────────────────────
    if (isSurprise && surpriseVariant === "guidedMeditation") {
      switch (currentScreen) {
        case 0:
        case 1:
          return (
            <CommonContent
              key={currentScreen}
              screenIndex={currentScreen}
              breatheContent={breatheContent}
              isAudioPlaying={isAudioPlaying}
            />
          );
        case 2:
          return breatheContent.guidedMeditation ? (
            <GuidedMeditationContent
              videoUrl={breatheContent.guidedMeditation}
              onEnded={() => {
                setSessionModalType("guided");
                setShowSessionModal(true);
              }}
            />
          ) : null;
        case 3:
          return (
            <CommonContent
              screenIndex={3}
              breatheContent={breatheContent}
              isAudioPlaying={isAudioPlaying}
              isLoopMusic={false}
            />
          );
        case 4:
          return (
            <EmojiContent
              breatheContent={breatheContent}
              selectedEmoji={selectedEmoji}
              setSelectedEmoji={setSelectedEmoji}
              onDone={handleClose}
            />
          );
        default:
          return null;
      }
    }

    // ── Surprise: mindful moment ─────────────────────────────────────────
    if (isSurprise && surpriseVariant === "mindfulMoment") {
      switch (currentScreen) {
        case 0:
        case 1:
          return (
            <CommonContent
              key={currentScreen}
              screenIndex={currentScreen}
              breatheContent={breatheContent}
              isAudioPlaying={isAudioPlaying}
            />
          );
        case 2:
          return breatheContent.mindfulMoment ? (
            <MindfulMomentContent
              mindfulMoment={breatheContent.mindfulMoment}
              exercise={breatheContent.exercises[2]}
              phase={mindfulPhase}
              timer={timer}
              isAudioPlaying={isAudioPlaying}
              onStart={() => setMindfulPhase("listening")}
              handleClose={handleClose}
            />
          ) : null;
        case 3:
          return (
            <CommonContent
              screenIndex={3}
              breatheContent={breatheContent}
              isAudioPlaying={isAudioPlaying}
              isLoopMusic
            />
          );
        case 4:
          return (
            <EmojiContent
              breatheContent={breatheContent}
              selectedEmoji={selectedEmoji}
              setSelectedEmoji={setSelectedEmoji}
              onDone={handleClose}
            />
          );
        default:
          return null;
      }
    }

    // ── Regular mindful breathing flow ───────────────────────────────────
    switch (currentScreen) {
      case 0:
      case 1:
        return (
          <CommonContent
            key={currentScreen}
            screenIndex={currentScreen}
            breatheContent={breatheContent}
            isAudioPlaying={isAudioPlaying}
          />
        );
      case 2:
        return (
          <RainbowContent
            breatheContent={breatheContent}
            isAudioPlaying={isAudioPlaying}
            timer={timer}
            isLoopMusic
          />
        );
      case 3:
        if (isDance || isMusic) return null;
        return (
          <CommonContent
            screenIndex={3}
            breatheContent={breatheContent}
            isAudioPlaying={isAudioPlaying}
            isLoopMusic
          />
        );
      case 4:
        return (
          <EmojiContent
            breatheContent={breatheContent}
            selectedEmoji={selectedEmoji}
            setSelectedEmoji={setSelectedEmoji}
            onDone={handleClose}
          />
        );
      default:
        return null;
    }
  };

  const showBottomYeti = !((currentScreen === 2 && isDance) || isMusic);

  // Mode dialog replaces the entire screen — no z-index fighting
  if (showModeDialog) {
    return (
      <div className="breathe">
        <ModeDialog onSelect={handleModeSelect} onClose={handleClose} />
      </div>
    );
  }

  return (
    <div className="breathe">
      {!(
        isSurprise &&
        surpriseVariant === "mindfulMoment" &&
        currentScreen === 2
      ) && (
        <div className="breathe__header">
          <div />
          <h2 className="breathe__title">
            {isSurprise &&
            surpriseVariant === "guidedMeditation" &&
            currentScreen === 2
              ? "Let's relax!"
              : (breatheContent?.label ?? "")}
          </h2>
          <button
            type="button"
            className="breathe__close"
            onClick={handleClose}
            aria-label="Close"
          >
            <img src={closeIcon} alt="Close" className="breathe__close-icon" />
          </button>
        </div>
      )}

      <div className="breathe__content">
        {loading ? <div className="breathe__spinner" /> : renderContent()}
      </div>

      {showBottomYeti &&
        breatheContent?.bottomImage &&
        !(
          isSurprise &&
          surpriseVariant === "mindfulMoment" &&
          currentScreen === 2
        ) && (
          <div className="breathe__yeti">
            <Lottie
              animationData={yetiBlink}
              loop
              autoplay
              className="breathe__yeti-lottie"
            />
          </div>
        )}

      {showSessionModal && (
        <SessionModal
          variant={sessionModalType}
          onEndSession={handleEndSession}
          onContinue={handleContinue}
        />
      )}
    </div>
  );
}

export default BreatheExcercise;
